export const COMMAND_STATUS = Object.freeze({
   PENDING: "pending",
   RUNNING: "running",
   SUCCEEDED: "succeeded",
   FAILED: "failed",
   TIMEOUT: "timeout",
   CANCELLED: "cancelled",
});

export const COMMAND_TYPE = Object.freeze({
   CREATE: "create",
   DELETE: "delete",
   PASTE: "paste",
   MOVE: "move",
   RESIZE: "resize",
   BLOCK: "block",
   UNBLOCK: "unblock",
});

/** Safe to re-execute after timeout/network blip without duplicating server state. */
export const IDEMPOTENT_COMMAND_TYPES = new Set([
   COMMAND_TYPE.DELETE,
   COMMAND_TYPE.BLOCK,
   COMMAND_TYPE.UNBLOCK,
]);

export function isIdempotentCommandType(type) {
   return IDEMPOTENT_COMMAND_TYPES.has(String(type || "").trim());
}

export const DEFAULT_COMMAND_TIMEOUT_MS = 30_000;
export const DEFAULT_COMMAND_MAX_RETRIES = 3;
export const DEFAULT_COMMAND_RETRY_DELAY_MS = 400;

export function createCommandId(prefix = "cmd") {
   return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * @typedef {Object} CalendarCommand
 * @property {string} id
 * @property {string} type
 * @property {object} payload
 * @property {object} [snapshot]
 * @property {string} status
 * @property {number} retryCount
 * @property {number} maxRetries
 * @property {number} timeoutMs
 * @property {number} createdAt
 * @property {number|null} startedAt
 * @property {number|null} completedAt
 * @property {unknown} result
 * @property {unknown} error
 */

export function createCalendarCommand({
   type,
   payload = {},
   snapshot = null,
   id = "",
   maxRetries = DEFAULT_COMMAND_MAX_RETRIES,
   timeoutMs = DEFAULT_COMMAND_TIMEOUT_MS,
} = {}) {
   const commandType = String(type || "").trim();
   if (!commandType) {
      throw new Error("createCalendarCommand requires a type");
   }
   return {
      id: id || createCommandId(commandType),
      type: commandType,
      payload: payload && typeof payload === "object" ? { ...payload } : {},
      snapshot:
         snapshot && typeof snapshot === "object"
            ? JSON.parse(JSON.stringify(snapshot))
            : snapshot,
      status: COMMAND_STATUS.PENDING,
      retryCount: 0,
      maxRetries: Math.max(0, Number(maxRetries) || 0),
      timeoutMs: Math.max(1_000, Number(timeoutMs) || DEFAULT_COMMAND_TIMEOUT_MS),
      createdAt: Date.now(),
      startedAt: null,
      completedAt: null,
      result: null,
      error: null,
   };
}
